/**
 * Phase B/C world: visitable homes, the night market, festivals and trips.
 * Lines marked “English practice” stay in English in every locale on purpose.
 */
export const enWorld3 = {
  world3: {
    mapNames: { grandma: 'Grandma Lee’s house', bo: 'Bo’s home', market: 'Lantern Night Market' },
    zones: { grandma: 'Grandma Lee’s house', bo: 'Bo’s home', market: 'Night market', marketGames: 'Game stalls' },
    objects: {
      grTea: 'Tea table',
      grCooker: 'Rice cooker',
      grPhotos: 'Family photos',
      grSewing: 'Sewing machine',
      grCalendar: 'Tear-off calendar',
      boConsole: 'Game console',
      boAquarium: 'Aquarium',
      boComics: 'Comic shelf',
      boLego: 'Building blocks',
      mkTea: 'Bubble tea stall',
      mkChicken: 'Fried chicken stall',
      mkBalls: 'Sweet potato balls',
      mkTofu: 'Stinky tofu stall',
      mkOyster: 'Oyster omelette stall',
      mkIce: 'Shaved ice stall',
      mkFish: 'Goldfish scooping',
      mkRings: 'Ring toss',
      mkClaw: 'Claw machines',
      marketGate: 'Night market gate',
      fsRiddles: 'Lantern riddles',
      fsTangyuan: 'Tangyuan cart',
      fsBoat: 'Dragon boats',
      fsZongzi: 'Zongzi table',
      fsEgg: 'Egg-standing table',
      fsBBQ: 'Moon Festival barbecue',
      fsPomelo: 'Pomelo basket',
      fsLion: 'Lion dance',
      fsReunion: 'New Year’s Eve dinner',
    },
    npc: { boMom: 'Bo’s mom', teaVendor: 'Auntie Hua', chickenVendor: 'Uncle Kang', gameVendor: 'Uncle Lou', shopper1: 'Shopper', shopper2: 'Shopper', shopper3: 'Shopper' },

    closed: {
      grandma: 'Nobody answers. Grandma Lee is usually home in the evening, after 6 pm.',
      bo: 'Nobody’s home. Bo’s family gets back in the late afternoon.',
      market: 'The night market opens at 5 pm. Come back this evening!',
    },

    grandma: {
      hello: 'Come in, come in! Take your shoes off and sit down. Would you like some tea?',
      teaText: 'Grandma pours oolong tea into tiny cups. “Smell it first, then sip slowly.”',
      drink: '🍵 Drink tea with Grandma',
      teaDone: 'Warm and fragrant. Grandma tells you about the tea hills where she grew up.',
      teaWords: 'English practice: “This tea smells lovely. Thank you, Grandma!”',
      cooker: 'The famous red rice cooker. Grandma says it’s older than your mom — and it still makes the best steamed buns.',
      steam: '🥟 Steam buns together',
      steamDone: 'Fluffy pork buns! Grandma wraps two for your family.',
      photos: 'Old black-and-white photos: Grandma as a girl on a farm, a wedding, a baby in a red blanket…',
      readStory: '📖 Listen to Grandma’s story',
      sewing: 'An old sewing machine with a foot pedal. Clack-clack-clack!',
      sewLearn: '🧵 Learn to sew a button',
      sewDone: 'You sewed your first button! Grandma gives you a little hand-made scarf.',
      calendar: (date: string) => `A thick tear-off calendar. Today’s page: ${date}. It also says which days are lucky!`,
      tearPage: '📅 Tear off today’s page',
      tearDone: 'Rrrip! Grandma laughs: “That’s the best part of the day.”',
      noGrandma: 'Grandma Lee isn’t here right now.',
      mochi: 'Mochi the kitten is curled up in a basket, purring.',
      byeGift: 'Before you leave, Grandma gives you a bag of oranges. “For your family!”',
    },
    bo: {
      momHello: 'Oh, it’s Bo’s friend! Thank you for helping him with the kite. Want some dumplings?',
      momDumplings: '🥟 Yes, please! (in English: “Yes, please. They smell great!”)',
      momDone: 'Juicy dumplings with a little vinegar. Bo’s mom smiles: “Come again soon!”',
      console: 'Bo’s game console. “Let’s play together!”',
      playConsole: '🎮 Play a game',
      aquarium: 'Neon tetras and a lazy little turtle. Bo named the turtle “Rocket”.',
      feedFish: '🐟 Feed the fish',
      fed: 'The fish dart up to the flakes. Rocket the turtle doesn’t hurry.',
      comics: 'Bo’s comics, in Chinese and in English. Some pages have doodles in the margins.',
      readComic: '📖 Read a comic',
      lego: 'A box of building blocks and a half-built space station.',
      build: '🧱 Build with Bo',
      built: 'You and Bo finish the space station. “Three, two, one… blast off!”',
      boAway: 'Bo isn’t home right now.',
    },

    fest: {
      names: { lantern: 'Lantern Festival', dragonBoat: 'Dragon Boat Festival', moon: 'Moon Festival', newYear: 'Lunar New Year' },
      announce: (name: string) => `🎉 It’s ${name} week! Go to the park to celebrate.`,
      riddles: {
        intro: 'Red lanterns with riddles hang everywhere. Guess right to win a little prize! (The riddles are in English.)',
        guess: '🏮 Guess a riddle',
        right: '🎉 Correct! The lantern keeper gives you a sticker.',
        wrong: 'Not this time — read it again!',
        allDone: 'You solved every riddle today. Come back tomorrow!',
        list: [
          { q: 'I have hands but I can’t clap. What am I?', a: ['A clock', 'A tree', 'A shoe'] },
          { q: 'The more you take, the more you leave behind. What am I?', a: ['Footsteps', 'Cookies', 'Money'] },
          { q: 'I’m tall when I’m young and short when I’m old. What am I?', a: ['A candle', 'A dog', 'A book'] },
          { q: 'What has keys but can’t open any door?', a: ['A piano', 'A car', 'A teacher'] },
          { q: 'What goes up but never comes down?', a: ['Your age', 'A ball', 'Rain'] },
          { q: 'What has a neck but no head?', a: ['A bottle', 'A snake', 'A chair'] },
          { q: 'I’m full of holes, but I can still hold water. What am I?', a: ['A sponge', 'A cup', 'A net'] },
          { q: 'What gets wetter the more it dries?', a: ['A towel', 'The sun', 'A fish'] },
          { q: 'What can you catch but not throw?', a: ['A cold', 'A ball', 'A bus'] },
        ],
      },
      tangyuan: 'Warm tangyuan — sweet rice balls in ginger soup. Round like the full moon, they mean the family is together.',
      eatTangyuan: '🥣 Eat a bowl',
      tangyuanDone: 'Chewy and sweet, with black-sesame filling. Your tummy feels warm!',
      boat: 'Two long dragon boats rock on the pond. The drummer waves: “Want to join the race?”',
      race: '🐉 Join the race',
      zongzi: 'Bamboo leaves, sticky rice, mushrooms and peanuts. Let’s wrap zongzi! What comes first?',
      zongziSteps: [
        { q: 'Step 1?', a: ['Fold two bamboo leaves into a cone', 'Tie the string', 'Steam it'] },
        { q: 'Step 2?', a: ['Fill it with rice and filling', 'Eat it', 'Throw the leaves away'] },
        { q: 'Step 3?', a: ['Close the leaves and tie them with string', 'Put it in the fridge', 'Paint it'] },
      ],
      zongziWrong: 'Hmm, not that yet. Think again!',
      zongziDone: 'Your zongzi go into the big steamer. An hour later: delicious!',
      egg: 'An old tradition: at exactly noon on Dragon Boat Festival, they say you can stand an egg on its end!',
      tryEgg: '🥚 Try to stand an egg',
      eggNoon: 'It’s not noon yet — come back between 11:00 and 13:00!',
      eggWin: 'It stands! Everyone claps. Good luck for the whole year!',
      eggFail: 'Wobble… wobble… it rolls over. Try again!',
      bbq: 'Neighbours grill corn, sausages and squid on little barbecues while the moon rises.',
      joinBBQ: '🌽 Join the barbecue',
      bbqDone: 'You share mooncakes and grilled corn under the full moon. “Happy Moon Festival!”',
      pomelo: 'A basket of big pomelos. Kids wear the peel as a funny hat!',
      pomeloHat: '🍈 Make a pomelo hat',
      pomeloDone: 'You wear a pomelo-peel hat. Everyone laughs — and the fruit is juicy and sweet!',
      lion: 'Drums and cymbals! A golden lion dances, jumps and blinks its big eyes.',
      watchLion: '🦁 Watch and cheer',
      lionDone: 'The lion bows to you and shakes its mane. That means good luck!',
      reunion: 'The whole family around the table: fish, dumplings, hot pot and long noodles for a long life.',
      eatReunion: '🍲 Have the reunion dinner',
      reunionDone: 'Mom and Dad give you a red envelope: +88 coins! Say it in English: “Happy New Year! Thank you!”',
      reunionAgain: 'You already had the reunion dinner this New Year.',
    },

    stories: {
      title: '📖 Story time',
      pick: 'Pick a story. Tap 🔊 to hear any line, or let me read it all to you.',
      readAll: '▶ Read it to me',
      stop: '⏹ Stop',
      gistBtn: '🌐 What is it about?',
      quiz: '❓ Questions',
      question: (n: number, total: number) => `Question ${n} of ${total}`,
      right: '✅ Correct!',
      wrong: '❌ Not quite — look at the story again.',
      result: (score: number, total: number) => `You got ${score} of ${total} right!`,
      again: '🔁 Read again',
      list: '📚 All stories',
      locked: '🔒 Find this story somewhere in the world',
      read: '✅ Read',
      gists: {
        kite: 'Bo’s kite gets stuck in a tree, and a friend helps him get it back.',
        mochi: 'Grandma Lee’s kitten gets lost chasing a butterfly.',
        market: 'Lily is shy, but she orders bubble tea in English at the night market.',
        dragon: 'How dragon boat races work — teamwork wins!',
        grandma: 'Grandma Lee remembers growing up on a tea farm.',
        comic: 'A funny comic: a little turtle builds a rocket.',
        tower: 'Facts about Taipei 101: the fast elevator and the giant golden ball.',
        gorge: 'Taroko Gorge in Hualien: marble cliffs and a turquoise river.',
      },
    },

    market: {
      welcome: '🏮 Welcome to the night market! Order your food in English for a smile (and sometimes a little extra).',
      look: 'Just looking',
      orderPrompt: 'How do you order? Pick the best English sentence.',
      wrong: [
        'The vendor laughs: “Ha! Try saying it nicely!”',
        '“Sorry? Could you say that again, more politely?”',
        'The vendor raises an eyebrow. Try a friendlier sentence!',
      ],
      cost: (n: number) => `${n} coins`,
      poor: 'You don’t have enough coins.',
      extra: '“You speak great English! Here’s a little extra for you.”',
      playFor: (n: number) => `▶ Play (${n} coins)`,
      gamesText: 'Lights, bells and prizes! One game costs a few coins.',
      clawText: 'A row of glowing claw machines full of round, fluffy plushies.',
      crowd: [
        'So many smells! I can’t decide what to eat first.',
        'My favourite is the sweet potato balls. Crunchy outside, chewy inside!',
        'Have you tried the stinky tofu? It smells strong, but it tastes great.',
        'I won a plushie at the claw machine! Well… after twelve tries.',
        'Don’t forget to keep your rubbish and throw it in the bins.',
        'The goldfish uncle says the secret is to move the scoop slowly.',
      ],
      stalls: {
        mkTea: {
          sign: '珍珠奶茶',
          food: 'bubble tea',
          vendor: 'Hi there! What can I get you?',
          ok: 'Could I have a bubble tea with less sugar, please?',
          bad: ['Give me tea.', 'Bubble! Bubble! Now!'],
          done: 'Sweet, cold and full of chewy pearls. Slurp!',
          price: 30,
        },
        mkChicken: {
          sign: '炸雞排',
          food: 'fried chicken cutlet',
          vendor: 'Fresh chicken cutlet, bigger than your face! Spicy or not spicy?',
          ok: 'Not spicy, please. Can you cut it for me?',
          bad: ['Chicken. Fast.', 'I want the biggest one for free.'],
          done: 'Crispy, juicy and HUGE. You need two hands!',
          price: 40,
        },
        mkBalls: {
          sign: '地瓜球',
          food: 'sweet potato balls',
          vendor: 'Sweet potato balls, hot and crunchy! Small bag or big bag?',
          ok: 'A small bag, please. Thank you!',
          bad: ['Balls! Give!', 'I don’t like potatoes, give me a bag.'],
          done: 'They puff up like little golden balloons. Crunchy and chewy!',
          price: 20,
        },
        mkTofu: {
          sign: '臭豆腐',
          food: 'stinky tofu',
          vendor: 'Stinky tofu! It smells strong, but it’s delicious. Want to try?',
          ok: 'Sure, I’d like to try it, please!',
          bad: ['Ew, it stinks!', 'Make it not stink.'],
          done: 'Crispy outside, soft inside, with pickled cabbage. Surprisingly yummy!',
          price: 35,
        },
        mkOyster: {
          sign: '蚵仔煎',
          food: 'oyster omelette',
          vendor: 'Oyster omelette with sweet sauce! For here or to go?',
          ok: 'For here, please. One oyster omelette.',
          bad: ['Omelette now!', 'I want it to go and to stay.'],
          done: 'Eggy, gooey and a little bit sweet. A night market classic!',
          price: 45,
        },
        mkIce: {
          sign: '芒果冰',
          food: 'mango shaved ice',
          vendor: 'Mango shaved ice — mountains of mango! Would you like condensed milk on top?',
          ok: 'Yes, please! Could I also have an extra spoon for my friend?',
          bad: ['Ice. Mango. Go.', 'No milk, no mango, just give me ice.'],
          done: 'Fluffy ice, sweet mango and a cold, happy brain freeze!',
          price: 50,
        },
      },
    },
  },
};
